import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CarouselNavigation } from "@talladega/components";
import {
  ActionButton,
  CmsButtonType,
  GalleryAndContentCarouselLayout,
} from "@talladega/types";
import { GalleryAndContent } from "../GalleryAndContent/GalleryAndContent";

interface Props extends GalleryAndContentCarouselLayout {
  urlHtmlId: string | null;
  cmsButton: (
    setButtonModalState: React.Dispatch<
      React.SetStateAction<{
        show: boolean;
        actionType: ActionButton;
      }>
    >,
    button: CmsButtonType,
    htmlIdRef?: React.MutableRefObject<HTMLDivElement | null>
  ) => React.ReactNode;
  cloudflareImage: (options: {
    src?: string;
    alt?: string;
    fill?: boolean;
    width?: number;
    height?: number;
    className?: string;
  }) => React.ReactNode;
  cmsButtonModalContainer: (buttonModalState: {
    show: boolean;
    actionType: ActionButton;
  }) => React.ReactNode;
  baseDomain: string;
}

export const GalleryAndContentCarousel = ({
  galleryAndContentRepeater,
  isSubContent,
  htmlIdRef,
  urlHtmlId,
  cmsButton,
  cloudflareImage,
  cmsButtonModalContainer,
  baseDomain,
}: Props) => {
  const [emblaRef, embla] = useEmblaCarousel({
    align: "start",
    active: galleryAndContentRepeater && galleryAndContentRepeater.length > 1,
    loop: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const galleryContentCarouselRef = useRef<HTMLDivElement>(null);

  const scrollPrev = useCallback(() => {
    return embla && embla.scrollPrev();
  }, [embla]);
  const scrollNext = useCallback(() => {
    return embla && embla.scrollNext();
  }, [embla]);
  const scrollTo = useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  useEffect(() => {
    if (!embla) return undefined;
    if (isAutoScrolling) {
      const timer = setInterval(() => {
        if (embla.canScrollNext()) {
          embla.scrollNext();
        } else {
          embla.scrollTo(0);
        }
      }, 5000); // Change slides every 5 seconds
      return () => clearInterval(timer);
    }

    return () => undefined;
  }, [embla, isAutoScrolling]);

  const handleInteractionStart = useCallback(
    () => setIsAutoScrolling(false),
    []
  );
  const handleInteractionEnd = useCallback(() => setIsAutoScrolling(true), []);

  return (
    <div id="cms-gallery-and-content-repeater">
      <div
        className="mx-auto flex max-w-page flex-col items-center gap-6"
        ref={
          galleryAndContentRepeater[0]?.galleryAndContentGroup?.htmlId ===
          urlHtmlId
            ? htmlIdRef
            : undefined
        }
      >
        <div className="w-full flex-1">
          <div
            className="relative"
            ref={galleryContentCarouselRef}
            role="button"
            tabIndex={0}
            onMouseDown={handleInteractionStart}
            onMouseUp={handleInteractionEnd}
            onMouseLeave={handleInteractionEnd}
          >
            <div className="w-full overflow-hidden" ref={emblaRef}>
              <div className="flex select-none">
                {galleryAndContentRepeater.map((content, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${content.galleryAndContentGroup.content}-${index}`}
                    className="w-full flex-none"
                  >
                    <GalleryAndContent
                      layout={{
                        ...content,
                        isSubContent,
                        htmlIdRef: undefined,
                      }}
                      cmsButton={cmsButton}
                      cloudflareImage={cloudflareImage}
                      cmsButtonModalContainer={cmsButtonModalContainer}
                      baseDomain={baseDomain}
                    />
                  </div>
                ))}
              </div>
            </div>
            {galleryAndContentRepeater.length > 1 && (
              <div className="absolute bottom-4 left-0 flex w-full justify-center gap-x-3">
                <CarouselNavigation
                  buttonColours="secondary"
                  contentList={galleryAndContentRepeater}
                  scrollNext={scrollNext}
                  scrollPrev={scrollPrev}
                  scrollTo={scrollTo}
                  selectedIndex={selectedIndex}
                  hiddenOnMobile
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default {};
